import { Edge, Node } from 'reactflow';
import { NodeData } from './bot-data';
import { TemplateComponentFormat, TemplateComponentTypes, VariablesType } from './template';

export type BotNode = Node<NodeData>;

export interface Bot {
  id: string;
  name: string;
  nodes: BotNode[];
  edges: Edge[];
  status: BotStatus;
  dateCreated: string;
}

export interface BotListData {
  dateCreated: string;
  dateUpdated: string;
  id: string;
  integrationId: string | null;
  orgId: string;
  name: string;
  status: BotStatus;
  runs: number;
  integrationIds: string[];
  createdBy: string;
  lastUpdatedBy: string | null;
  channelType?: string;
}

export enum BotStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface WarningType {
  show: boolean;
  message: string;
}

export interface HelpData {
  description?: string;
  videoLink?: string;
  /** Help-center article; resolved on the FE from the block type */
  articleLink?: string;
  title?: string;
}

export interface DynamicMenuItem {
  id: string;
  title: string;
  disabled?: boolean;
  value: string;
}

export interface ComparableData {
  edges?: Edge[];
  nodes?: Node[];
  values?: any;
}

export interface BotWithID {
  botId: string;
  botName: string;
}

export interface Trigger {
  triggerId: string;
  triggerName: string;
  bots: BotWithID[];
}

export interface SetTemplateDataArgs {
  variables: string[];
  type: VariablesType;
  cardIndex?: number;
  index?: number;
  totalNumOfBodyVariables?: number;
}

export interface SetTemplateHeaderMediaArgs {
  mediaUrl: string | null;
  mediaType?: Exclude<TemplateComponentFormat, TemplateComponentFormat.TEXT>;
  fileName?: string;
  cardIndex?: number;
  isDeleted?: boolean;
}

export type CardVariables = Array<{
  type: VariablesType;
  name: string;
  meta?: string;
  isOptional?: boolean;
  cardData?: {
    cardIndex: number;
    componentType: TemplateComponentTypes.BODY | TemplateComponentTypes.BUTTON | TemplateComponentTypes.HEADER;
    buttonIndex?: number;
  };
}>;
export interface PlanAddon {
  addonId: string;
  startDate: string;
  endDate: string;
}

export enum PlanAddonTypes {
  Tally = 'Tally-Integration',
  ActivityLogs = 'Activity-Logs',
  ComplexBot = 'Complex-Bot',
  WooCommerce = 'Woo-Commerce',
  SlaAddon = 'SLA-Addon',
  AiBot = 'AI-Bot',
  EnterpriseAnalytics = 'Enterprise-Analytics',
  Salesforce = 'Salesforce',
  LeadSquaredIntegration = 'LeadSquared-Integration-Addon',
  CapiSupportAddon = 'Capi-Support-Addon',
  WaVoiceAddon = 'WA-CALL',
  CallingLicense = 'Calling-License',
}

export enum FunctionTypes {
  CONTACT_SUPPORT = 'CONTACT_SUPPORT',
}

export interface PiiSettings {
  textPolicy?: 'SEND' | 'REJECT' | 'MASK';
  mediaPolicy?: 'SEND' | 'REJECT' | 'MASK';
  maskApiConfig?: {
    media?: {
      url: string;
      authorization?: string;
    };
    text?: {
      url: string;
      authorization?: string;
    };
  };
}

interface ExpiredSubscribedPlan {
  id: string;
  numUsers: number;
  displayName: string;
  currencyCode: string;
  canMakeNextPayment: true;
  renewalDate: string;
  willPaymentAutoRenew: boolean;
  customPricing: boolean;
  status: 'active' | 'cancelled' | 'expired' | 'payment_progress' | 'expired_grace';
  price: [
    {
      id: string;
      amount: number;
      unit: 'month' | 'year';
    }
  ];
  function?: {
    type: FunctionTypes;
  };
  addons: PlanAddon[];
  piiSettings?: PiiSettings;
}

interface UnexpiredSubscribedPlan {
  canMakeNextPayment: false;
  addons?: PlanAddon[];
  piiSettings?: PiiSettings;
}

export type SubscribedPlan = ExpiredSubscribedPlan | UnexpiredSubscribedPlan;

export enum AssignAgentDropdownOptions {
  LAST_ASSIGNED_AGENT = 'LAST_ASSIGNED_AGENT',
  ROUND_ROBIN = 'ROUND_ROBIN',
  RANDOM_ASSIGNMENT = 'RANDOM_ASSIGNMENT',
  RING_SIMULTANEOUSLY = 'RING_SIMULTANEOUSLY',
  AI_AGENT = 'AI_AGENT',
}

export enum IntegrationType {
  QUICKSELL = 'Quicksell',
  WHATSAPP_CLOUD = 'Whatsapp Cloud Api',
  WHATSAPP_BUSINESS = 'Whatsapp Business Solution',
  INSTAGRAM = 'INSTAGRAM',
  OPENAI = 'OpenAI',
  ZOHO_CRM = 'ZOHO_CRM',
  TALLY = 'Tally',
}

interface MetaDataType {
  orgName: string;
  email: string;
  orgId: string;
  country: string | null;
  shopDomain?: string;
}

export interface Integration {
  id: string | null;
  name: string;
  type: IntegrationType;
  integrationSlug?: string;
  metadata?: MetaDataType;
  status: string;
  feature?: string;
}

export interface IntegrationDetails extends Integration {
  credentials: {
    apiKey?: string;
    appSecret?: string;
    channelId?: string;
    phoneName?: string;
    phoneNumber?: string;
    status?: string;
  };
}

export interface ChannelDisplaySettings {
  logoUrl: string;
  logoBgColor: string;
  description: string;
}

export enum ChannelType {
  WHATSAPP = 'WHATSAPP',
  INSTAGRAM = 'INSTAGRAM',
}

export interface ConversationChannelConfig {
  type: string;
  displayName: string;
  capabilities: string[];
  canInitiateConversation: boolean;
  supportedMessageTypes: string[];
  isVisible: boolean;
  manage: string[];
  botBuilder?: { enabled?: boolean };
  displaySettings: ChannelDisplaySettings | null;
  onboarding?: Array<{
    type: string;
    url: string;
    headerTitle: string;
    title: string;
    description: string;
  }>;
}

export interface AvailableChannelsResponse {
  configs: ConversationChannelConfig[];
}
